import React, { useState } from "react";
import styled from "styled-components";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { colors } from "utils";
import { LiveQuestionModal } from "./LiveQuestionModal";
import cornersOutIcon from "assets/Icon-CornersOut.svg";
import cornersInIcon from "assets/Icon-CornersIn.svg";
import { LiveSessionPanel } from "./LiveSessionPanel";
import { SafetyConcernsTag } from "../shared/criticalIncidentsUi";

// Tokens pulled from Figma variables (OKO Teacher Report).
const T = {
  red600: "#DC2626",
  red300: "#FCA5A5",
  red50: "#FEF2F2",
  yellow600: "#CA8A04",
  yellow300: "#FDE047",
  yellow50: "#FEFCE8",
  green600: "#00A35A",
  green300: "#86EFAC",
  green50: "#F0FDF4",
  pink500: colors.pink500,
  pink300: colors.pink300,
  pink50: colors.pink50,
  blue800: colors.blue800,
  blue300: colors.blue300,
  blue50: "#EEEEF9",
  neutral100: "#F2F4F7",
  neutral300: "#D0D5DD",
  greenInk: colors.nightlyWoods950,
  inkMuted: colors.nightlyWoods500,
  white: "#FFFFFF",
  offWhite: "#FAF7F2",
};

const formatStartedAt = (iso) => {
  if (!iso) return "Started recently";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Started recently";
  const weekday = d.toLocaleDateString(undefined, { weekday: "short" });
  const date = d.toLocaleDateString(undefined, {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
  });
  const time = d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${weekday}, ${date} • ${time}`;
};

const studentFallbackName = (id) =>
  id ? `Student ${String(id).slice(-4).toUpperCase()}` : "Student";

const truncate = (s, max) =>
  s && s.length > max ? `${s.slice(0, max).trimEnd()}…` : s;

const groupCodeFallback = (groupId) =>
  groupId ? `…${String(groupId).slice(-5).toUpperCase()}` : "Group";

// The code a pill and its filter chip show; early events can lack group_code.
export const groupCodeLabel = (session) =>
  (session.group_code || groupCodeFallback(session.group_id)).toUpperCase();

// Normalizes the various shapes question_standards[0] arrives in: plain string,
// { code, title }, { standard_code, name }, etc. Returns {code, title} where
// either may be null.
const extractStandard = (raw) => {
  if (!raw) return { code: null, title: null };
  if (typeof raw === "string") return { code: raw, title: null };
  if (typeof raw === "object") {
    const code =
      raw.code || raw.standard_code || raw.standardCode || raw.id || null;
    const title =
      raw.title || raw.name || raw.description || raw.standard_title || null;
    return { code, title };
  }
  return { code: null, title: null };
};

// Prefer session_started_at from the backend (v2.1 contract). Fall back to the
// oldest loop's occurred_at for older payloads or sessions whose start time
// wasn't captured.
const inferStartedAt = (session) => {
  if (session?.session_started_at) return session.session_started_at;
  if (session?.started_at) return session.started_at;
  if (!session?.window?.length) return null;
  const sorted = [...session.window].sort((a, b) =>
    String(a.occurred_at).localeCompare(String(b.occurred_at)),
  );
  return sorted[0]?.occurred_at || null;
};

const collabBucket = (level) => {
  if (level == null) return "unknown";
  if (level <= 1) return "low";
  if (level === 2) return "mid";
  if (level === 3) return "high";
  return "top";
};

const LiveSessionPill = ({
  session,
  classroomName,
  expanded = false,
  onToggle,
  onEndSession,
  transcriptLines,
  snapshotCount,
}) => {
  const [modalQuestionId, setModalQuestionId] = useState(null);
  const openQuestion = (questionId) => (e) => {
    // Don't bubble up to the pill's onClick.
    e.stopPropagation();
    setModalQuestionId(questionId);
  };
  const closeModal = () => setModalQuestionId(null);

  // Window is newest-first; the Figma reads oldest → newest left → right.
  const window = session.window || [];
  const ordered = [...window].slice(0, 6).reverse();
  const latest = window[0] || null;

  // Alerts reflect ONLY the three most recent NON-solo-time questions — they
  // clear as soon as the streak breaks. Solo-time questions (group answered
  // correctly in Solo Time → collaboration N/A) are dropped from the comparison
  // entirely, so a solo-time question landing in the middle (or at the end) of a
  // streak doesn't reset it — the warning still fires on the 3 most recent real
  // questions. We derive these client-side rather than trusting the server's
  // *_alert_raised flags, which stay sticky after the streak ends.
  const isSoloTime = (loop) => loop.is_na || loop.collaboration_level == null;
  const lastThree = ordered.filter((loop) => !isSoloTime(loop)).slice(-3);
  const profRaised =
    lastThree.length === 3 &&
    lastThree.every((loop) => !loop.proficiency_aligned);
  const collabRaised =
    lastThree.length === 3 &&
    lastThree.every((loop) => loop.collaboration_level <= 1);
  // Safety is sticky by design, so the server flag is used as-is.
  const safetyRaised = Boolean(session.safety_concern_raised);
  const anyAlert = collabRaised || profRaised || safetyRaised;

  const groupCode = groupCodeLabel(session);
  const startedAt = inferStartedAt(session);
  // Mirror SessionSummary.js:326 — prefer session-level standard_prefix +
  // standard_prefix_description (or _name), fall back to per-loop
  // question_standards if the session-level fields aren't populated.
  const standardCode =
    session.standard_prefix ||
    extractStandard((latest?.question_standards || [])[0]).code ||
    null;
  const standardTitle =
    session.standard_prefix_name ||
    extractStandard((latest?.question_standards || [])[0]).title ||
    null;

  // Participants is the session-wide roster (ships on session_started). Falls
  // back to the latest loop's submissions if the backend hasn't populated it.
  const baseRoster =
    (session.participants && session.participants.length > 0
      ? session.participants
      : latest?.submissions) || [];
  // Merge mid-session participant_update events on top of the base roster.
  // participantOverrides is keyed by student_id and is never overwritten by
  // update events, so late joiners and disconnects survive question loop resets.
  const overrides = session.participantOverrides || {};
  const roster = (() => {
    const merged = [...baseRoster];
    for (const override of Object.values(overrides)) {
      const idx = merged.findIndex((x) => x.student_id === override.student_id);
      if (idx === -1) {
        merged.push(override);
      } else {
        merged[idx] = { ...merged[idx], ...override };
      }
    }
    return merged;
  })();
  const students = roster.map((s) => ({
    name: s.student_name || s.display_name || studentFallbackName(s.student_id),
    connected: s.connected !== false,
  }));

  const panelId = `live-panel-${session.session_id}`;

  return (
    <Card $alert={anyAlert} data-testid="live-pill">
    {/* Mouse convenience only; the expand button is the keyboard and
        screen-reader control, so the header stays a plain, non-interactive div. */}
    <Pill $expanded={expanded} $clickable={Boolean(onToggle)} onClick={onToggle}>
      <LeftCol>
        <GroupTitle>
          Group Session: <GroupCode>{groupCode}</GroupCode>
        </GroupTitle>
        <DateLine>
          {classroomName ? `${classroomName} · ` : ""}
          {formatStartedAt(startedAt)}
        </DateLine>
        {safetyRaised && (
          <TagRow>
            <SafetyConcernsTag />
          </TagRow>
        )}
      </LeftCol>

      <Divider />

      <MiddleCol>
        <Row>
          <RowLabel>
            <MiddleTitle>Live Updates</MiddleTitle>
          </RowLabel>
          <Cells aria-label="Questions">
            {ordered.length === 0 ? (
              <MutedSmall>Working on first problem</MutedSmall>
            ) : (
              ordered.map((loop) => (
                <CellSlot key={`q-${loop.question_id}`}>
                  <QLabel
                    type="button"
                    onClick={openQuestion(loop.question_id)}
                  >
                    Q{loop.question_order}
                  </QLabel>
                </CellSlot>
              ))
            )}
          </Cells>
        </Row>
        <Row $bg={profRaised ? T.pink50 : undefined}>
          <RowLabel>
            {profRaised && (
              <IconSlot>
                <TipBubble
                  id="prof-alert-tt"
                  title="This group may need support"
                  body="They've missed 3 questions in a row."
                >
                  <AlertTriangleIcon ariaLabel="Proficiency alert raised" />
                </TipBubble>
              </IconSlot>
            )}
            Proficiency
          </RowLabel>
          <Cells>
            {ordered.map((loop) => (
              <CellSlot key={`p-${loop.question_id}`}>
                <ProficiencyCell
                  state={loop.proficiency_aligned ? "aligned" : "miss"}
                />
              </CellSlot>
            ))}
          </Cells>
        </Row>
        <Row $bg={collabRaised ? T.pink50 : undefined}>
          <RowLabel>
            {collabRaised && (
              <IconSlot>
                <TipBubble
                  id="collab-alert-tt"
                  title="This group may need support"
                  body="They've received Level 1 (Free Agents) or Level 0 (Stealth Mode) for 3 questions in a row."
                >
                  <AlertTriangleIcon ariaLabel="Collaboration alert raised" />
                </TipBubble>
              </IconSlot>
            )}
            Collaboration
          </RowLabel>
          <Cells>
            {ordered.map((loop) => {
              const showNa = loop.is_na || loop.collaboration_level == null;
              const hex = (
                <CollabHex
                  level={showNa ? null : loop.collaboration_level}
                  bucket={showNa ? "na" : collabBucket(loop.collaboration_level)}
                />
              );
              return (
                <CellSlot key={`c-${loop.question_id}`}>
                  {showNa ? (
                    <TipBubble
                      id={`na-tt-${loop.question_id}`}
                      body="Not Applicable, as the group got the answer right in Solo Time."
                    >
                      {hex}
                    </TipBubble>
                  ) : (
                    hex
                  )}
                </CellSlot>
              );
            })}
          </Cells>
        </Row>
      </MiddleCol>

      <Divider />

      <RightCol>
        <RightHeader>
          <SectionLabel>
            {standardCode ? (
              <>
                Students working on {standardCode}
                {standardTitle && (
                  <>
                    :{" "}
                    <SectionLabelSuffix title={standardTitle}>
                      {truncate(standardTitle, 80)}
                    </SectionLabelSuffix>
                  </>
                )}
              </>
            ) : (
              "Students in this session"
            )}
          </SectionLabel>
        </RightHeader>
        {students.length === 0 ? (
          <MutedSmall>
            Student roster will appear once the first question is answered.
          </MutedSmall>
        ) : (
          <StudentsGrid $rows={Math.min(students.length, 3)}>
            {students.map(({ name, connected }, i) =>
              connected ? (
                <StudentRow key={i}>
                  <StudentName title={name}>{name}</StudentName>
                </StudentRow>
              ) : (
                <StudentRow key={i}>
                  <TipBubble
                    id={`disconnected-tt-${i}`}
                    body="This student is disconnected"
                    placement="bottom"
                  >
                    <DisconnectedStudent>
                      <WarnIconSlot>
                        <AlertTriangleIcon ariaLabel="Student disconnected" />
                      </WarnIconSlot>
                      <StudentName title={name} $disconnected>{name}</StudentName>
                    </DisconnectedStudent>
                  </TipBubble>
                </StudentRow>
              ),
            )}
          </StudentsGrid>
        )}
      </RightCol>
      {onToggle && (
        <ExpandCol>
          <ExpandBtn
            type="button"
            aria-label={expanded ? "Collapse session" : "Expand session"}
            aria-expanded={expanded}
            aria-controls={expanded ? panelId : undefined}
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
          >
            <img src={expanded ? cornersInIcon : cornersOutIcon} alt="" aria-hidden="true" />
          </ExpandBtn>
        </ExpandCol>
      )}
    </Pill>
    {expanded && (
      <LiveSessionPanel
        id={panelId}
        session={session}
        transcriptLines={transcriptLines}
        snapshotCount={snapshotCount}
        onEndSession={onEndSession}
      />
    )}
    {/* Outside the clickable header: portal clicks bubble through the React
        tree and would otherwise toggle the card. */}
    <LiveQuestionModal
      show={modalQuestionId != null}
      onHide={closeModal}
      session={session}
      initialQuestionId={modalQuestionId}
    />
    </Card>
  );
};

export default LiveSessionPill;

// ─── icon primitives ──────────────────────────────────────────────────────────

// Filled red triangle with a white "!" — exact SVG from Figma (node 2391:22373).
// Rendered only when the corresponding alert is raised (3 consecutive incorrect
// loops for proficiency, 3 consecutive level 0/1 loops for collaboration).
const AlertTriangleIcon = ({ ariaLabel }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    role="img"
    aria-label={ariaLabel}
  >
    <path
      d="M10.2679 5C11.0377 3.66667 12.9623 3.66667 13.7321 5L20.6603 17C21.4301 18.3333 20.4678 20 18.9282 20H5.0718C3.5322 20 2.56995 18.3333 3.33975 17L10.2679 5Z"
      fill="#DC2626"
    />
    <path
      d="M13.2143 8L13.0274 14.8727H11.2732L11.0815 8H13.2143ZM12.1503 17.94C11.834 17.94 11.5624 17.8282 11.3356 17.6045C11.1087 17.3777 10.9969 17.1061 11.0001 16.7898C10.9969 16.4766 11.1087 16.2082 11.3356 15.9846C11.5624 15.7609 11.834 15.6491 12.1503 15.6491C12.4538 15.6491 12.7206 15.7609 12.9507 15.9846C13.1807 16.2082 13.2974 16.4766 13.3005 16.7898C13.2974 17.0006 13.2414 17.1939 13.1328 17.3697C13.0274 17.5422 12.8884 17.6812 12.7158 17.7866C12.5433 17.8889 12.3548 17.94 12.1503 17.94Z"
      fill="white"
    />
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden>
    <path
      d="M5 12.5l4 4 10-10"
      fill="none"
      stroke={T.white}
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const XIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden>
    <path
      d="M6 6l12 12M18 6L6 18"
      fill="none"
      stroke={T.white}
      strokeWidth="3"
      strokeLinecap="round"
    />
  </svg>
);

// White, rounded, shadowed tooltip matching Figma nodes 2621:9235,
// 2461:34011, 2461:34012. Bold lead + regular body via {title, body} props.
const TipBubble = ({ id, title, body, children, placement = "top" }) => (
  <OverlayTrigger
    placement={placement}
    delay={{ show: 150, hide: 100 }}
    overlay={
      <LivePillTooltip id={id}>
        <TipInner>
          {title && <TipTitle>{title}</TipTitle>}
          {title && body ? " " : null}
          {body && <TipBody>{body}</TipBody>}
        </TipInner>
      </LivePillTooltip>
    }
  >
    <TipTarget>{children}</TipTarget>
  </OverlayTrigger>
);

const ProficiencyCell = ({ state }) =>
  state === "aligned" ? (
    <TipBubble
      id="prof-correct-tt"
      title="Correct:"
      body="All students submitted the correct answer"
    >
      <Circle $bg={T.green600} $border={T.green600}>
        <CheckIcon />
      </Circle>
    </TipBubble>
  ) : (
    <TipBubble
      id="prof-incorrect-tt"
      title="Incorrect:"
      body="one or more students did not submit the correct answer"
    >
      <Circle $bg={T.pink500} $border={T.pink500}>
        <XIcon />
      </Circle>
    </TipBubble>
  );

const CollabHex = ({ level, bucket }) => {
  // Palette pulled from Figma node 2461:33184 (na cell) and 2545:100279
  // (empty/pending cell). Both use Grey/500 fill + Grey/800 border; N/A
  // additionally renders the literal "N/A" text in Grey/950.
  const palette = {
    low:  { fill: T.pink50,   border: T.pink300,   text: colors.pink800, fontSize: 18 },
    mid:  { fill: T.yellow50, border: T.yellow300, text: "#9B6B03",      fontSize: 18 },
    high: { fill: T.green50,  border: "#76D9AD",   text: "#006B3B",      fontSize: 18 },
    top:  { fill: T.blue50,   border: T.blue300,   text: T.blue800,      fontSize: 18 },
    na: { fill: T.offWhite, border: "#B9B7B3", text: "#6B6967", fontSize: 11 },
    unknown: { fill: T.offWhite, border: "#B9B7B3", text: null, fontSize: 18 },
  };
  const p = palette[bucket] || palette.unknown;
  const label =
    bucket === "na"
      ? "N/A"
      : p.text != null && level != null
        ? String(level === 0 ? 1 : level)
        : null;
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 32 32"
      role="img"
      aria-label={
        bucket === "na"
          ? "Not applicable — group solved in solo time"
          : level != null
            ? `Collaboration level ${level === 0 ? 1 : level}`
            : "Collaboration score pending"
      }
    >
      <polygon
        points="16,2 29,9.5 29,22.5 16,30 3,22.5 3,9.5"
        fill={p.fill}
        stroke={p.border}
        strokeWidth="1.5"
      />
      {label != null && (
        <text
          x="16"
          y={bucket === "na" ? 20 : 22.5}
          textAnchor="middle"
          fontFamily="Inter, sans-serif"
          fontSize={p.fontSize}
          fontWeight="700"
          fill={p.text}
        >
          {label}
        </text>
      )}
    </svg>
  );
};

// ─── styled primitives ────────────────────────────────────────────────────────

// The card carries the border (green, red on any alert); the header row and
// the panel it opens sit inside it.
const Card = styled.div`
  border-radius: 12px;
  background: ${T.neutral100};
  border: 2px solid ${({ $alert }) => ($alert ? T.red600 : colors.green500)};
  box-shadow: 0 1px 2px 0 rgba(14, 26, 49, 0.05);
  overflow: hidden;
`;

const Pill = styled.div`
  display: grid;
  /* Fixed left meta column, Live Updates at its content width, students take
     the rest. Divider columns size to their own width + margins (1px line +
     16px each side). Last column: expand button. */
  grid-template-columns: 205px auto auto auto 1fr auto;
  column-gap: 0;
  row-gap: 16px;
  align-items: stretch;
  padding: 16px 24px;
  background: ${({ $expanded }) => ($expanded ? "#FEFEFD" : "transparent")};
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
  @media (max-width: 1024px) {
    /* Laptop (≤1024px): stack the columns (and the expand button) into one,
       turning the vertical dividers into full-width horizontal rules, and fan
       the cell row across the full width so the Q-labels and badges stay
       column-aligned. */
    grid-template-columns: 1fr;
    & > [data-divider] {
      width: auto;
      height: 1px;
      margin: 8px 0;
    }
    [data-cells] {
      justify-content: space-between;
    }
  }
`;

const ExpandCol = styled.div`
  display: flex;
  align-items: center;
  margin-left: 16px;
  @media (max-width: 1024px) {
    justify-content: flex-end;
    margin-left: 0;
  }
`;

const ExpandBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  padding: 8px;
  border: none;
  border-radius: 6px;
  background: ${colors.blue500};
  cursor: pointer;

  img {
    width: 24px;
    height: 24px;
  }

  &:hover {
    background: #4b48d9;
  }

  &:focus-visible {
    outline: 2px solid ${colors.blue800};
    outline-offset: 2px;
  }
`;

const Divider = styled.div.attrs({ "data-divider": "" })`
  background: #FFFFFF;
  width: 1px;
  margin: 0 16px;
`;

const LeftCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const GroupTitle = styled.div`
  font-family: Inter, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 26px;
  letter-spacing: 0.25px;
  color: ${T.greenInk};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const GroupCode = styled.span`
  font-weight: 700;
  color: ${T.greenInk};
`;


const DateLine = styled.div`
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: ${T.greenInk};
`;

const TagRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  flex-wrap: wrap;
`;

const MiddleCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 0;
`;

const MiddleTitle = styled.span`
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: ${T.greenInk};
`;

// Figma node 2100:18778 — Body/Small/Regular Underline (Inter 14/20, weight 400)
const QLabel = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: ${colors.blue500};
  text-decoration: underline;
  text-underline-position: from-font;
  &:hover { opacity: 0.75; }
  &:focus-visible {
    outline: 2px solid ${colors.blue500};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

// Each row shares the same grid: a fixed label column on the left, then a
// cells column on the right. CellSlot inside Cells has a fixed width so Q
// labels stack neatly above the proficiency / collaboration badges.
const Row = styled.div`
  display: grid;
  grid-template-columns: 117px 1fr;
  align-items: center;
  gap: 10px;
  border-radius: 4px;
  padding: 4px;
  background: ${({ $bg }) => $bg || "transparent"};
`;

const RowLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: ${T.greenInk};
`;

// Reserves a fixed slot so labels stay vertically aligned whether or not the
// warning icon is currently visible.
const IconSlot = styled.span`
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Cells = styled.div.attrs({ "data-cells": "" })`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const CellSlot = styled.div`
  width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Circle = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${({ $bg }) => $bg};
  border: 1px solid ${({ $border }) => $border};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MutedSmall = styled.span`
  font-family: Inter, sans-serif;
  font-size: 12px;
  color: ${T.inkMuted};
`;

const RightCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const RightHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;

// Matches SessionSummary.js:644 (SectionLabel + SectionLabelSuffix) so the
// live pill's standard header reads identically to the historical pill.
const SectionLabel = styled.p`
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: ${T.greenInk};
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

const SectionLabelSuffix = styled.span`
  color: ${T.greenInk};
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
`;

// Mirrors SessionSummary.js:728 — column-flow grid so names fill column-by-column
// (3 students with $rows=3 → 1 column; 9 students → 3 cols × 3 rows).
const StudentsGrid = styled.div`
  display: grid;
  grid-template-rows: repeat(${({ $rows }) => $rows || 1}, auto);
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);
  gap: 4px 16px;
`;

const StudentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

// Wraps the warning icon + name for a disconnected student so the tooltip
// target spans both and they stay baseline-aligned.
const DisconnectedStudent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const WarnIconSlot = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
`;

const StudentName = styled.span`
  font-family: Inter, sans-serif;
  font-size: 14px;
  color: ${({ $disconnected }) => ($disconnected ? T.red600 : T.greenInk)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// White tooltip with shadow — matches Figma nodes 2621:9235 / 2461:34011 / 34012.
const LivePillTooltip = styled(Tooltip)`
  opacity: 1 !important;
  pointer-events: none;

  /* !important overrides the global .tooltip .tooltip-inner rule in
     Educator/Dashboard/dashboard.css, which forces a dark green background. */
  .tooltip-inner {
    background: #FFFFFF !important;
    color: ${T.greenInk} !important;
    border-radius: 20px;
    padding: 12px 16px;
    text-align: left;
    max-width: 280px;
    font-family: Inter, sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0;
    box-shadow:
      0 4px 4px -2px rgba(12, 12, 13, 0.08),
      0 16px 32px -2px rgba(12, 12, 13, 0.16);
  }

  &.bs-tooltip-top .tooltip-arrow::before,
  &.bs-tooltip-auto[x-placement^="top"] .tooltip-arrow::before {
    border-top-color: #FFFFFF !important;
  }
  &.bs-tooltip-bottom .tooltip-arrow::before,
  &.bs-tooltip-auto[x-placement^="bottom"] .tooltip-arrow::before {
    border-bottom-color: #FFFFFF !important;
  }
  &.bs-tooltip-left .tooltip-arrow::before,
  &.bs-tooltip-auto[x-placement^="left"] .tooltip-arrow::before {
    border-left-color: #FFFFFF !important;
  }
  &.bs-tooltip-right .tooltip-arrow::before,
  &.bs-tooltip-auto[x-placement^="right"] .tooltip-arrow::before {
    border-right-color: #FFFFFF !important;
  }
`;

// OverlayTrigger needs a ref-forwarding element; wrap inline icons in a span.
const TipTarget = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const TipInner = styled.span`
  display: inline;
`;

const TipTitle = styled.span`
  font-weight: 700;
`;

const TipBody = styled.span`
  font-weight: 400;
`;
